<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import GovernmentSidebar from "@/components/government/GovernmentSidebar.vue";
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <GovernmentSidebar />
    <div id="main-content" class="flex-1 flex flex-col">
      <!-- Header -->
      <header id="header" class="bg-white border-b border-light-gray p-5">
        <div class="flex justify-between items-center">
          <div>
            <h2 class="text-2xl font-medium text-dark-navy">
              {{ $t('government.doe_licenses.title') }}
            </h2>
            <p class="text-gray-600">
              {{ $t('government.doe_licenses.subtitle') }}
            </p>
          </div>
          <div class="flex items-center space-x-4">
            <button
              class="px-4 py-2 bg-forest-green text-white rounded-md hover:bg-green-700"
            >
              <i class="fa-solid fa-plus mr-2"></i>
              {{ $t('government.doe_licenses.new_license') }}
            </button>
            <button
              class="p-2 rounded-md border border-light-gray hover:bg-gray-50"
            >
              <i class="fa-solid fa-bell text-gray-600"></i>
            </button>
            <div class="flex items-center space-x-3">
              <img
                src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                alt="Admin"
                class="w-8 h-8 rounded-full"
              />
              <span class="text-sm font-medium">{{ $t('government.dashboard_page.admin_user') }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- DOE Licenses Content -->
      <main id="doe-licenses-main" class="flex-1 p-5 overflow-y-auto">
        <!-- Search and Filters -->
        <div
          id="search-filters"
          class="bg-white p-5 rounded-md border border-light-gray mb-6"
        >
          <div class="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >{{ $t('government.doe_licenses.search_filters.search_label') }}</label
              >
              <div class="relative">
                <input
                  type="text"
                  :placeholder="$t('government.doe_licenses.search_filters.search_placeholder')"
                  class="w-full pl-10 pr-4 py-2 border border-light-gray rounded-md"
                />
                <i
                  class="fa-solid fa-search absolute left-3 top-3 text-gray-400"
                ></i>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >{{ $t('government.doe_licenses.search_filters.status_label') }}</label
              >
              <select
                class="w-full px-3 py-2 border border-light-gray rounded-md"
              >
                <option>{{ $t('government.doe_licenses.search_filters.all_status') }}</option>
                <option>{{ $t('government.doe_licenses.search_filters.active') }}</option>
                <option>{{ $t('government.doe_licenses.search_filters.pending') }}</option>
                <option>{{ $t('government.doe_licenses.search_filters.suspended') }}</option>
                <option>{{ $t('government.doe_licenses.search_filters.expired') }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >{{ $t('government.doe_licenses.search_filters.license_type_label') }}</label
              >
              <select
                class="w-full px-3 py-2 border border-light-gray rounded-md"
              >
                <option>{{ $t('government.doe_licenses.search_filters.all_types') }}</option>
                <option>{{ $t('government.doe_licenses.search_filters.validation') }}</option>
                <option>{{ $t('government.doe_licenses.search_filters.verification') }}</option>
                <option>{{ $t('government.doe_licenses.search_filters.both') }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >{{ $t('government.doe_licenses.search_filters.actions_label') }}</label
              >
              <div class="flex space-x-2">
                <button
                  class="px-3 py-2 bg-electric-blue text-dark-navy rounded-md text-sm hover:bg-cyan-400"
                >
                  <i class="fa-solid fa-filter mr-1"></i>
                  {{ $t('government.doe_licenses.search_filters.filter') }}
                </button>
                <button
                  class="px-3 py-2 border border-light-gray rounded-md text-sm hover:bg-gray-50"
                >
                  <i class="fa-solid fa-download mr-1"></i>
                  {{ $t('government.doe_licenses.search_filters.export') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- License Statistics -->
        <div
          id="license-stats"
          class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
        >
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.doe_licenses.stats.total_licenses') }}</p>
                <p class="text-2xl font-medium text-dark-navy">42</p>
              </div>
              <i
                class="fa-solid fa-file-contract text-forest-green text-xl"
              ></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.doe_licenses.stats.active_licenses') }}</p>
                <p class="text-2xl font-medium text-forest-green">35</p>
              </div>
              <i class="fa-solid fa-check-circle text-forest-green text-xl"></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.doe_licenses.stats.pending_review') }}</p>
                <p class="text-2xl font-medium text-electric-blue">4</p>
              </div>
              <i class="fa-solid fa-clock text-electric-blue text-xl"></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.doe_licenses.stats.expiring_soon') }}</p>
                <p class="text-2xl font-medium text-error-red">3</p>
              </div>
              <i
                class="fa-solid fa-exclamation-triangle text-error-red text-xl"
              ></i>
            </div>
          </div>
        </div>

        <!-- Licenses Table -->
        <div
          id="licenses-table"
          class="bg-white rounded-md border border-light-gray overflow-hidden"
        >
          <div class="p-5 border-b border-light-gray">
            <h3 class="text-lg font-medium text-dark-navy">
              {{ $t('government.doe_licenses.table.title') }}
            </h3>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.doe_licenses.table.columns.organization') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.doe_licenses.table.columns.license_id') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.doe_licenses.table.columns.type') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.doe_licenses.table.columns.contact') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.doe_licenses.table.columns.status') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.doe_licenses.table.columns.expiry_date') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {{ $t('government.doe_licenses.table.columns.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div class="text-sm font-medium text-gray-900">
                        Carbon Verification Ltd
                      </div>
                      <div class="text-sm text-gray-500">
                        123 Green Street, Capital City
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    DOE-2024-001
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                      >{{ $t('government.doe_licenses.table.type_labels.validation') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900">John Smith</div>
                    <div class="text-sm text-gray-500">
                      j.smith@carbonverify.com
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                      >{{ $t('government.doe_licenses.table.status_labels.active') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    2025-12-31
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-cyan-600 mr-3">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                    <button class="text-error-red hover:text-red-700">
                      <i class="fa-solid fa-ban"></i>
                    </button>
                  </td>
                </tr>
                <tr>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div class="text-sm font-medium text-gray-900">
                        EcoAudit International
                      </div>
                      <div class="text-sm text-gray-500">
                        456 Sustainability Ave, Metro City
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    DOE-2024-002
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs"
                      >{{ $t('government.doe_licenses.table.type_labels.both') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900">Sarah Johnson</div>
                    <div class="text-sm text-gray-500">
                      s.johnson@ecoaudit.org
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs"
                      >{{ $t('government.doe_licenses.table.status_labels.pending') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    2025-06-15
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-cyan-600 mr-3">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                    <button class="text-error-red hover:text-red-700">
                      <i class="fa-solid fa-ban"></i>
                    </button>
                  </td>
                </tr>
                <tr>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div class="text-sm font-medium text-gray-900">
                        Green Standards Corp
                      </div>
                      <div class="text-sm text-gray-500">
                        789 Climate Road, Eco City
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    DOE-2024-003
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                      >{{ $t('government.doe_licenses.table.type_labels.verification') }}</span
                    >
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900">Michael Chen</div>
                    <div class="text-sm text-gray-500">
                      m.chen@greenstandards.com
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                      >{{ $t('government.doe_licenses.table.status_labels.expiring') }}</span
                    >
                  </td>
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm text-error-red"
                  >
                    2024-08-30
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button class="text-forest-green hover:text-green-700 mr-3">
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button class="text-electric-blue hover:text-cyan-600 mr-3">
                      <i class="fa-solid fa-edit"></i>
                    </button>
                    <button class="text-error-red hover:text-red-700">
                      <i class="fa-solid fa-ban"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div id="pagination" class="px-6 py-3 border-t border-light-gray">
            <div class="flex items-center justify-between">
              <div class="text-sm text-gray-500">
                {{ $t('government.doe_licenses.pagination.showing') }} 1 {{ $t('government.doe_licenses.pagination.to') }} 3 {{ $t('government.doe_licenses.pagination.of') }} 42 {{ $t('government.doe_licenses.pagination.results') }}
              </div>
              <div class="flex space-x-1">
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
                >
                  {{ $t('government.doe_licenses.pagination.previous') }}
                </button>
                <button
                  class="px-3 py-1 bg-forest-green text-white rounded-md text-sm"
                >
                  1
                </button>
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
                >
                  2
                </button>
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
                >
                  3
                </button>
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
                >
                  {{ $t('government.doe_licenses.pagination.next') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>
